import type { CursoAcademy, ProgresoFila } from './academyApi';

/**
 * El progreso de un curso tal como lo consumen las pantallas — derivado de la
 * fila cruda de `academy_progreso` más el curso, que es el que sabe cuántas
 * lecciones hay (ADR-2026-08-25-la-academia-vive-en-supabase).
 *
 * Antes esto lo calculaba `progresoAcademy.ts` sobre `localStorage`. Se mantiene
 * la misma forma a propósito: las pantallas no cambiaron su lectura del
 * progreso, sólo de dónde sale.
 */
export interface ProgresoCurso {
  /** Lecciones terminadas. */
  vistas: number;
  /**
   * Los ids de las lecciones terminadas.
   *
   * Va además del contador porque el panel deja saltar a cualquier lección: con
   * sólo el número había que decidir "vista" por índice, y quien mira la 3ª
   * primero terminaba con la 1ª tildada. **El progreso no es un cursor, es un
   * conjunto.**
   */
  vistasIds: string[];
  total: number;
  /** 0–100, redondeado. */
  porcentaje: number;
  /** Vio todas las lecciones — habilita el examen. */
  completo: boolean;
  aprobado: boolean;
  mejorPuntaje: number | null;
  /** Cuándo rindió por última vez, en ms de época, o `null`. */
  ultimoIntento: number | null;
  /** La primera lección sin ver, o la última si ya vio todas. */
  siguienteLeccionId: string;
}

/** Un curso sin ninguna fila de progreso: todo en cero. */
export function progresoVacio(curso: CursoAcademy): ProgresoCurso {
  return derivarProgreso(curso, undefined);
}

export function derivarProgreso(
  curso: CursoAcademy,
  fila: ProgresoFila | undefined,
): ProgresoCurso {
  const vistasIds = new Set(fila?.leccionesVistas ?? []);
  // Se cuenta contra el CURSO y no contra lo guardado: si una lección se da de
  // baja, su id queda en la fila y el progreso pasaría de 100%.
  const presentes = curso.lecciones.filter((l) => vistasIds.has(l.id));
  const total = curso.lecciones.length;
  const pendiente = curso.lecciones.find((l) => !vistasIds.has(l.id));

  return {
    vistas: presentes.length,
    vistasIds: presentes.map((l) => l.id),
    total,
    porcentaje: total === 0 ? 0 : Math.round((presentes.length / total) * 100),
    completo: total > 0 && presentes.length === total,
    aprobado: fila?.aprobado ?? false,
    mejorPuntaje: fila?.mejorPuntaje ?? null,
    ultimoIntento: fila?.ultimoIntentoAt
      ? new Date(fila.ultimoIntentoAt).getTime()
      : null,
    siguienteLeccionId:
      pendiente?.id ?? curso.lecciones[curso.lecciones.length - 1]?.id ?? '',
  };
}

/**
 * Cuántos milisegundos faltan para poder volver a rendir. `0` = se puede ahora.
 *
 * La espera REAL la hace cumplir el servidor (`academy_rendir_examen`); esto es
 * sólo para dibujar el contador. Que las dos usen el mismo dato —el instante del
 * último intento, que viene de la base— es lo que hace que no discrepen.
 *
 * **El transcurrido se acota a `[0, ∞)` y NO se habilita ante un instante futuro**
 * (ADR-2026-08-25-el-desfasaje-de-relojes-no-habilita-el-examen).
 *
 * `ultimoIntento` viene del reloj de la BASE y `ahora` del reloj del NAVEGADOR: son
 * dos máquinas distintas y difieren por unas decenas de milisegundos aun estando las
 * dos en hora. Medido en la app: el `rendido_at` que devuelve la corrección quedaba
 * 57 ms en el futuro respecto de `Date.now()`.
 *
 * La versión anterior devolvía `0` —examen habilitado— ante cualquier `transcurrido`
 * negativo, tratando ese desfasaje normal como un reloj roto. Efecto: apenas se
 * corregía un examen reprobado, el resultado ofrecía "Repetir examen" sobre una espera
 * que el servidor iba a hacer cumplir igual.
 *
 * Acotando a 0, un instante futuro cuenta la espera ENTERA, que además es un límite
 * mejor que el de antes: por lejos que esté el reloj, nunca se espera más que
 * `esperaMinutos`. La barrera de verdad sigue siendo el servidor.
 */
export function msParaProximoIntento(
  ultimoIntento: number | null,
  esperaMinutos: number,
  ahora: number = Date.now(),
): number {
  if (ultimoIntento === null || esperaMinutos <= 0) return 0;
  const esperaMs = esperaMinutos * 60 * 1000;
  const transcurrido = Math.max(0, ahora - ultimoIntento);
  if (transcurrido >= esperaMs) return 0;
  return esperaMs - transcurrido;
}
